<script setup lang="ts">
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import { Option } from '@/js/components/autoComplete/types';
    import EchoInfoBox from '@/js/components/EchoInfoBox.vue';
    import CreateUserFromAgentJobsTable from '@/js/back-office/components/CreateUserFromAgentJobsTable.vue';
    import CheckboxInput from '@/js/components/CheckboxInput.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import SelectInput from '@/js/components/SelectInput.vue';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import {
        useMutationEnqueueCreateUsersFromAgents,
        useMutationUpdateBackOfficeTenant,
        useQueryGetAgentsWithoutUser,
        useQueryGetAssignableTenantRoles,
        useQueryGetBackOfficeTenant,
        useQueryGetBackOfficeTenants,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import { __ } from '@/js/stores/expressions';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { CheckIcon, XIcon } from '@lucide/vue';
    import { computed, ref, watch, watchEffect } from 'vue';

    const successDialogStore = useSuccessDialog();
    const { data: tenants } = useQueryGetBackOfficeTenants();
    const tenantOptions = computed(() => {
        const options: Array<Option> = (tenants.value ?? []).map((x) => {
            return {
                name: x.name,
                value: x.id,
            };
        });
        return options;
    });

    const search = ref<string | null>(null);
    const tenantId = ref<string>('');
    const tenantName = ref<string>('');
    const createUserFromAgent = ref<boolean>(false);

    // vue does not support 'null' as a value for options, so we need another
    // model field just for the role ID.
    const defaultAgentRoleId = ref<string | undefined>(undefined);

    // Separate from defaultAgentRoleId above - this one is only for a single "send individually"
    // run and is never persisted. Defaults to the tenant's saved role (see the tenant watcher
    // below) but can be changed per-run without touching the saved config.
    const sendRoleId = ref<string | undefined>(undefined);

    const showAgentTable = ref(false);
    const selectedIds = ref<Record<string, boolean>>({});
    const activeTab = ref<'jobs' | 'send'>('jobs');

    function onSelectTenant(opt: Option): void {
        tenantId.value = opt.value;
        tenantName.value = opt.name;
        showAgentTable.value = false;
        activeTab.value = 'jobs';
    }

    function onClearTenant(): void {
        tenantId.value = '';
        tenantName.value = '';
        createUserFromAgent.value = false;
        defaultAgentRoleId.value = undefined;
        search.value = null;
        showAgentTable.value = false;
        activeTab.value = 'jobs';
    }

    const { data: tenant } = useQueryGetBackOfficeTenant(tenantId);
    const { data: roles } = useQueryGetAssignableTenantRoles(tenantName);
    const updateTenantMut = useMutationUpdateBackOfficeTenant();

    async function submit(): Promise<unknown> {
        if (!tenant.value) {
            return Promise.reject();
        }
        await updateTenantMut.mutateAsync({
            ...tenant.value,
            createUserFromAgent: createUserFromAgent.value,
            defaultAgentRoleId: defaultAgentRoleId.value || null,
        });

        successDialogStore.openDialog({
            message: __('saved'),
        });
    }

    function onToggleCreateUserFromAgent(value: boolean) {
        createUserFromAgent.value = value;
    }

    watch(
        () => tenant.value,
        (newTenant, oldTenant) => {
            createUserFromAgent.value = !!newTenant?.createUserFromAgent;
            defaultAgentRoleId.value = newTenant?.defaultAgentRoleId || undefined;

            // Only re-derive sendRoleId when the tenant itself changes (a new customer was picked) -
            // this query also refetches after the unrelated top-section Save button succeeds, which
            // must not silently overwrite a role the user already picked in the "send individually"
            // tab for this run.
            if (newTenant?.id !== oldTenant?.id) {
                sendRoleId.value = newTenant?.defaultAgentRoleId || undefined;
            }
        }
    );

    // One-off: create users for existing agents that already have an email but no user yet.
    // Uses the same role field as the auto-create config above, independent of whether that
    // flag is enabled - a tenant may want a single backfill run without turning on ongoing auto-creation.
    const { data: agentsData, isLoading: agentsLoading } = useQueryGetAgentsWithoutUser(
        tenantId,
        showAgentTable
    );
    const agents = computed(() => agentsData.value ?? []);

    // Default-checks only agents not seen before, preserving any manual (un)checks across
    // refetches - this list refetches after the send action itself (which invalidates
    // AGENTS_WITHOUT_USER), so resetting every entry to checked here would silently re-select
    // agents the user had just deliberately unchecked.
    watch(
        agents,
        (newAgents) => {
            const next: Record<string, boolean> = { ...selectedIds.value };
            for (const agent of newAgents) {
                if (!(agent.id in next)) next[agent.id] = true;
            }
            selectedIds.value = next;
        },
        { immediate: true }
    );

    const allSelected = computed(
        () => agents.value.length > 0 && agents.value.every((a) => selectedIds.value[a.id])
    );
    const someSelected = computed(() => agents.value.some((a) => selectedIds.value[a.id]));
    const selectedCount = computed(
        () => agents.value.filter((a) => selectedIds.value[a.id]).length
    );

    const toggleAllSelected = () => {
        const newVal = !allSelected.value;
        for (const agent of agents.value) selectedIds.value[agent.id] = newVal;
    };

    const selectAllCheckbox = ref<HTMLInputElement | null>(null);
    watchEffect(() => {
        if (selectAllCheckbox.value) {
            selectAllCheckbox.value.indeterminate = someSelected.value && !allSelected.value;
        }
    });

    const { mutateAsync: enqueueCreateUsersMutation, isPending: createUsersIsPending } =
        useMutationEnqueueCreateUsersFromAgents();

    const canCreateUsers = computed(() => someSelected.value && !!sendRoleId.value);

    async function createUsersFromAgents(): Promise<void> {
        if (!canCreateUsers.value || !tenant.value) return;

        const selected = agents.value.filter((a) => selectedIds.value[a.id]);

        // Sent as an explicit override so this works even if the tenant has no saved
        // DefaultAgentRoleId - no need to save the dropdown selection to the tenant config first.
        const results = await enqueueCreateUsersMutation({
            tenantId: tenantId.value,
            agentIds: selected.map((agent) => agent.id),
            roleId: sendRoleId.value || undefined,
        });

        const enqueued = results.filter((r) => r.status === 'Enqueued').length;
        const skipped = results.length - enqueued;

        // Only mention the counts that are actually non-zero - e.g. skip the "N were skipped"
        // sentence entirely when everything got queued.
        const messageParts: string[] = [];
        if (enqueued > 0) {
            messageParts.push(
                __('back-office-create-users-from-agents-queued-enqueued', {
                    enqueued: String(enqueued),
                })
            );
        }
        if (skipped > 0) {
            messageParts.push(
                __('back-office-create-users-from-agents-queued-skipped', {
                    skipped: String(skipped),
                })
            );
        }

        successDialogStore.openDialog({
            message: messageParts.join(' '),
        });

        activeTab.value = 'jobs';
    }
</script>

<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-agent-config') }}</h1>
        </template>
        <template #topbar>
            <EchoButton
                variant="primary"
                :disabled="!tenantId || (createUserFromAgent && !defaultAgentRoleId)"
                @click="submit"
            >
                {{ __('save') }}
                <template #icon-right><CheckIcon /></template>
            </EchoButton>
            <EchoButton variant="secondary" @click="onClearTenant">
                {{ __('cancel') }}
                <template #icon-right><XIcon /></template>
            </EchoButton>
        </template>
        <template #content>
            <EchoInfoBox class="mb-6 max-w-2xl">
                {{ __('back-office-agent-config-info') }}
            </EchoInfoBox>

            <div class="w-lg max-w-full flex flex-col gap-6">
                <AutoComplete
                    v-model="search"
                    input-id="tenant-config-id"
                    :label="__('back-office-select-customer')"
                    :options="tenantOptions"
                    @select="onSelectTenant"
                    @clear="onClearTenant"
                />

                <div v-if="!!tenantId">
                    <div class="pb-3">
                        <CheckboxInput
                            id="create-users-from-agents"
                            v-model:model-value="createUserFromAgent"
                            @update:model-value="onToggleCreateUserFromAgent(!!$event)"
                        >
                            {{ __('back-office-create-user-from-agent') }}
                        </CheckboxInput>
                    </div>

                    <div class="flex flex-col gap-1">
                        <label class="text-overline" for="default-agent-role-id">
                            {{ __('back-office-default-agent-role') }}
                            <span v-if="createUserFromAgent" aria-hidden="true">*</span>
                        </label>
                        <SelectInput id="default-agent-role-id" v-model="defaultAgentRoleId">
                            <option value=""></option>
                            <option v-for="role in roles" :value="role.id" :key="role.id">
                                {{ role.name }}
                            </option>
                        </SelectInput>
                    </div>
                </div>
            </div>

            <div v-if="!!tenantId" class="mt-10 border-t border-gray-lightest pt-8">
                <h2 class="heading-sm mb-1">{{ __('back-office-create-users-from-agents') }}</h2>

                <div class="flex gap-4 border-b border-gray-lightest mt-4 mb-6">
                    <button
                        type="button"
                        class="pb-2 text-body-bold border-b-2 -mb-px"
                        :class="
                            activeTab === 'jobs'
                                ? 'border-primary-plum text-primary-plum'
                                : 'border-transparent text-gray-medium'
                        "
                        @click="activeTab = 'jobs'"
                    >
                        {{ __('back-office-create-users-from-agents-tab-jobs') }}
                    </button>
                    <button
                        type="button"
                        class="pb-2 text-body-bold border-b-2 -mb-px"
                        :class="
                            activeTab === 'send'
                                ? 'border-primary-plum text-primary-plum'
                                : 'border-transparent text-gray-medium'
                        "
                        @click="activeTab = 'send'"
                    >
                        {{ __('back-office-create-users-from-agents-tab-send') }}
                    </button>
                </div>

                <CreateUserFromAgentJobsTable v-if="activeTab === 'jobs'" :tenant-id="tenantId" />

                <template v-else>
                    <div class="flex flex-col gap-1 w-xs">
                        <label class="text-overline" for="send-role-id">
                            {{ __('back-office-create-users-from-agents-role') }}
                        </label>
                        <SelectInput id="send-role-id" v-model="sendRoleId">
                            <option value=""></option>
                            <option v-for="role in roles" :value="role.id" :key="role.id">
                                {{ role.name }}
                            </option>
                        </SelectInput>
                    </div>

                    <div class="flex items-center gap-4 mt-4">
                        <EchoButton
                            variant="secondary"
                            :loading="agentsLoading"
                            @click="showAgentTable = true"
                        >
                            {{ __('back-office-fetch-agents-without-user') }}
                        </EchoButton>
                        <template v-if="showAgentTable">
                            <span class="text-body-bold">
                                {{
                                    __('back-office-import-agents-selected-count', {
                                        selected: String(selectedCount),
                                        total: String(agents.length),
                                    })
                                }}
                            </span>
                            <EchoButton
                                variant="primary"
                                :disabled="!canCreateUsers"
                                :loading="createUsersIsPending"
                                @click="createUsersFromAgents"
                            >
                                {{ __('back-office-create-users-from-agents-submit') }}
                            </EchoButton>
                        </template>
                    </div>

                    <template v-if="showAgentTable">
                        <div class="mt-6 overflow-x-auto">
                            <table class="w-full border-collapse">
                                <thead>
                                    <tr class="border-b border-gray-lightest">
                                        <th class="py-3 pr-4 w-10 text-left align-middle">
                                            <EchoTooltip
                                                :label="__('back-office-import-agents-select-all')"
                                            >
                                                <input
                                                    ref="selectAllCheckbox"
                                                    type="checkbox"
                                                    class="Custom-checkbox cursor-pointer ml-2.5"
                                                    :checked="allSelected"
                                                    @change="toggleAllSelected"
                                                />
                                            </EchoTooltip>
                                        </th>
                                        <th
                                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle"
                                        >
                                            {{ __('users-table-column-name') }}
                                        </th>
                                        <th
                                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle"
                                        >
                                            {{ __('email') }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody class="relative">
                                    <tr v-if="agentsLoading">
                                        <td colspan="3" class="py-12 text-center">
                                            <LoadingDots />
                                        </td>
                                    </tr>
                                    <tr v-else-if="!agents.length">
                                        <td
                                            colspan="3"
                                            class="py-6 text-body text-gray-medium text-center"
                                        >
                                            {{ __('back-office-create-users-from-agents-none') }}
                                        </td>
                                    </tr>
                                    <tr
                                        v-for="agent in agents"
                                        :key="agent.id"
                                        class="border-b border-gray-lightest cursor-pointer hover:bg-gray-50"
                                        @click="selectedIds[agent.id] = !selectedIds[agent.id]"
                                    >
                                        <td class="py-3 pr-4 w-10" @click.stop>
                                            <input
                                                type="checkbox"
                                                class="Custom-checkbox cursor-pointer"
                                                :checked="selectedIds[agent.id]"
                                                @change="
                                                    selectedIds[agent.id] = !selectedIds[agent.id]
                                                "
                                            />
                                        </td>
                                        <td class="py-3 pl-3 pr-6 text-body">
                                            {{
                                                [agent.firstName, agent.lastName]
                                                    .filter(Boolean)
                                                    .join(' ')
                                            }}
                                        </td>
                                        <td class="py-3 pl-3 pr-6 text-body">
                                            {{ agent.email }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </template>
                </template>
            </div>
        </template>
    </SettingsPageLayout>
</template>
